import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterEventListener } from '@uhg-abyss/parcels/hooks/useRegisterEventListener';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useModal } from '@src/state/modal';
import { LagoonProvider } from '@sde/widgets-core/src/tools/lagoon';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { buildPPDLagoonTables } from '@sde/widgets-core/src/tools/lagoon/helpers';
import { Router } from '@uhg-abyss/web/ui/Router';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { ppdLagoon } from 'src/tools/lagoon';
import { App } from './App';
import { Home } from '../routes/Home';
import { ErrorPage } from './ErrorPage';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../routes/Home/Footer';
import { PrintProvider } from '@uhg-abyss/web/ui/PrintProvider';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import { event as abyssEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useBreadcrumbs } from '@src/state/breadcrumbs';
import { useAlerts } from '@src/state/alerts';
import { useDashboard } from '@src/state/dashboard';
import { useFileUpload } from '@sde/widgets-core/src/redux/resources/upload';
import { useOnboarding } from '@sde/widgets-core/src/redux/resources/onboarding';
import { usePayers } from '@sde/widgets-core/src/redux/resources/payers';
import { usePayer } from 'src/state/payer';
import { useProvider } from '@src/state/provider';
import { useSearchResults } from '@src/state/searchResults';
import { useTutorial } from '@src/state/tutorial';
import { useHeaderMenu } from '@src/state/headerMenu';
import { useGetUserAccountNumber } from '@sde/widgets-core/src/common/hooks/useGetUserAccountNumberQuery';
import { useCoreStore } from '@src/store/useCoreStore';
import { event } from '@uhg-abyss/web/tools/event';

const theme = createTheme('uhc', {
  enterpriseFont: true, css: {
    '.abyss-nav-menu-viewport-container': { // temp fix for global nav issue. resolved in 1.77.1, but upgrading requires a decision for patch package or release coordination
      perspective: 'none',
    },
  },
});

const StyledContainer = styled('div', {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: '$gray2',
  zIndex: 1200,
});

export const AppWrapper = ({
  isContentLoading,
  setIsContentLoading,
  digitalSecurityError,
  setDigitalSecurityError,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const app = pathname.split('/')[2];
  const isUhcpForms = pathname.includes('uhcp-forms');
  const isCodeLookup = pathname.includes('code-lookup');
  const navigate = useNavigate();
  const { togglePayer, toggleProvider } = useHeaderMenu.actions();
  const { toggleModal } = useModal.actions();
  const { getUserDetails } = useUser.actions();
  const { user } = useUser.state();
  const {
    setBreadcrumbs,
    removeBreadcrumb,
    removeBreadcrumbs,
    resetBreadcrumbs,
    breadcrumbs,
  } = useBreadcrumbs();
  const { addAlert, clearAlerts, showAlert } = useAlerts.actions();
  const {
    clearRecentSearches,
    openTrackItFlyout,
    setRecentSearch,
    setReferralCriteria,
    setSelectedPLNProvider,
    toggleOONWidget,
    searchCurrentMember,
  } = useDashboard.actions();
  const { clearResults: clearPostPdf, postPdf } = useFileUpload.actions();
  const { getOnboarding, setOnboarding } = useOnboarding.actions();
  const { getPayers } = usePayers.actions();
  const { setPayer } = usePayer.actions();
  const { setSearchResults } = useSearchResults.actions();
  const { setTutorialActive } = useTutorial.actions();
  const { setProvider } = useProvider.actions();
  const { setAccountNumber } = useCoreStore();

  const [isInitialized, setIsInitalized] = useState(false);

  const [initError, setInitError] = useState('');
  const [ppdLagoonTables, setPPDLagoonTables] = useState({});
  const accountDropdownNumberFeatureFlag = getFeature(
    'ACCOUNT_DROPDOWN_NUMBER'
  );
  const [, getUserAccountNumber] = useGetUserAccountNumber({
    onCompleted: (response) => {
      if (response.accountNumber) {
        setAccountNumber({
          value: response.accountNumber,
          error: false,
          show: true,
        });
        event.setData({ accountNumber: response.accountNumber });
      }
    },
    onError: (error) => {
      if (error) {
        if (error.code && error.code === 'DSA:V:ACN:NF') {
          setAccountNumber({ show: false });
        } else {
          setAccountNumber({ error: true });
        }
      }
    },
  });

  const handleToggleModal = useCallback((event) => {
    toggleModal(event.detail);
  }, []);

  const handleToggleProvider = useCallback((event) => {
    toggleProvider(event.detail);
  }, []);

  const handleTogglePayer = useCallback((event) => {
    togglePayer(event.detail);
  }, []);

  const handleGetUserDetails = useCallback((event) => {
    getUserDetails(event.detail);
  }, []);

  const handleSetBreadcrumbs = useCallback(
    (event) => {
      setBreadcrumbs(event.detail);
    },
    [breadcrumbs]
  );

  const handleParcelNavigate = useCallback(
    (event) => {
      if (event.detail) {
        navigate(event.detail);
      }
    },
    [navigate]
  );

  const handleAddAlert = useCallback((event) => {
    addAlert(event.detail);
  }, []);
  const handleClearAlerts = useCallback(() => {
    clearAlerts();
  }, []);
  const handleClearRecentSearches = useCallback(() => {
    clearRecentSearches();
  }, []);
  const handleClearPostPdf = useCallback(() => {
    clearPostPdf();
  }, []);
  const handleGetOnboarding = useCallback((event) => {
    getOnboarding(event.detail);
  }, []);
  const handleSetOnboarding = useCallback((event) => {
    setOnboarding(event.detail);
  }, []);
  const handleGetPayers = useCallback(() => {
    getPayers();
  }, []);
  const handleOpenTrackItFlyout = useCallback((event) => {
    openTrackItFlyout(event.detail);
  }, []);
  const handlePostPdf = useCallback((event) => {
    postPdf(event.detail);
  }, []);
  const handleRemoveBreadcrumb = useCallback((event) => {
    removeBreadcrumb(event.detail);
  }, []);
  const handleRemoveBreadcrumbs = useCallback(() => {
    removeBreadcrumbs();
  }, []);
  const handleResetBreadcrumbs = useCallback(() => {
    resetBreadcrumbs();
  }, []);
  const handleSetPayer = useCallback((event) => {
    setPayer(event.detail);
  }, []);
  const handleSetProvider = useCallback((event) => {
    setProvider(event.detail);
  }, []);
  const handleSetRecentSearch = useCallback((event) => {
    setRecentSearch(event.detail);
  }, []);
  const handleSetReferralCriteria = useCallback((event) => {
    setReferralCriteria(event.detail);
  }, []);
  const handleSetSearchResults = useCallback((event) => {
    setSearchResults(event.detail);
  }, []);
  const handleToggleOONWidget = useCallback(() => {
    toggleOONWidget();
  }, []);
  const handleSetSelectedPLNProvider = useCallback((event) => {
    setSelectedPLNProvider(event.detail);
  }, []);
  const handleSetTutorialActive = useCallback((event) => {
    setTutorialActive(event.detail);
  }, []);
  const handleShowAlert = useCallback((event) => {
    showAlert(event.detail);
  }, []);
  const handleSearchCurrentMember = useCallback((event) => {
    searchCurrentMember(event.detail);
  }, []);
  const handleCoreLiveChat = useCallback((event) => {
    abyssEvent('PRODUCT_LIVE_CHAT');
  }, []);

  // Redux event listeners
  useRegisterEventListener('parcelNavigate', handleParcelNavigate);
  useRegisterEventListener('toggleModal', handleToggleModal);
  useRegisterEventListener('toggleProvider', handleToggleProvider);
  useRegisterEventListener('togglePayer', handleTogglePayer);
  useRegisterEventListener('getUserDetails', handleGetUserDetails);
  useRegisterEventListener('setBreadcrumbs', handleSetBreadcrumbs);
  useRegisterEventListener('addAlert', handleAddAlert);
  useRegisterEventListener('clearAlerts', handleClearAlerts);
  useRegisterEventListener('clearRecentSearches', handleClearRecentSearches);
  useRegisterEventListener('clearPostPdf', handleClearPostPdf);
  useRegisterEventListener('getOnboarding', handleGetOnboarding);
  useRegisterEventListener('setOnboarding', handleSetOnboarding);
  useRegisterEventListener('getPayers', handleGetPayers);
  useRegisterEventListener('openTrackItFlyout', handleOpenTrackItFlyout);
  useRegisterEventListener('postPdf', handlePostPdf);
  useRegisterEventListener('removeBreadcrumb', handleRemoveBreadcrumb);
  useRegisterEventListener('removeBreadcrumbs', handleRemoveBreadcrumbs);
  useRegisterEventListener('resetBreadcrumbs', handleResetBreadcrumbs);
  useRegisterEventListener('setPayer', handleSetPayer);
  useRegisterEventListener('setProvider', handleSetProvider);
  useRegisterEventListener('setRecentSearch', handleSetRecentSearch);
  useRegisterEventListener('setReferralCriteria', handleSetReferralCriteria);
  useRegisterEventListener('setSearchResults', handleSetSearchResults);
  useRegisterEventListener('toggleOONWidget', handleToggleOONWidget);
  useRegisterEventListener(
    'setSelectedPLNProvider',
    handleSetSelectedPLNProvider
  );
  useRegisterEventListener('setTutorialActive', handleSetTutorialActive);
  useRegisterEventListener('showAlert', handleShowAlert);
  useRegisterEventListener('searchCurrentMember', handleSearchCurrentMember);
  useRegisterEventListener('coreLiveChat', handleCoreLiveChat);
  // Redux event listeners

  const DEFAULT_ERROR = `The system is unable to respond at the moment. \
  Please try refreshing the page (click on REFRESH at the top of the page \
  or press F5) or try again at a later time.`;

  // Function to update lagoon tables and window object
  const updatePPDLagoonTables = useCallback(() => {
    try {
      const updatedPpdLagoonTables = buildPPDLagoonTables(ppdLagoon);
      setPPDLagoonTables(updatedPpdLagoonTables);
      if (typeof window !== 'undefined') {
        window.ppdLagoon = updatedPpdLagoonTables;
      }
    } catch (error) {
      console.error('Error updating PPD Lagoon tables:', error);
    }
  }, []);

  // Expose refresh function to window for manual updates
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.refreshPpdLagoon = updatePPDLagoonTables;
    }
  }, [updatePPDLagoonTables]);

  useEffect(() => {
    Promise.all([ppdLagoon.init()])
      .then(([ppdLagoonRes]) => {
        if (!ppdLagoonRes) {
          throw new Error('Could not retrieve Lagoon data');
        }
        updatePPDLagoonTables();
        if (ppdLagoonRes.isInitialized) {
          setIsInitalized(true);
          // TODO: look into making auth script dynamic in the future.
          // commenting out to test session resolution
          // const script = document.createElement('script');
          // script.src = getFeature('OHID_SESSION_REWORK')
          //   ? config('UHC_OHID_AUTHJS_URL')
          //   : config('UHC_AUTHJS_URL');
          // script.onload = () => console.log('Script loaded successfully');
          // script.onerror = () => console.error('Failed to load the script');
          // document.body.appendChild(script);
        }
      })
      .catch(() => {
        setInitError(DEFAULT_ERROR);
      });
  }, []);

  // Update lagoon tables when user state changes (login/logout)
  useEffect(() => {
    if (isInitialized && user) {
      updatePPDLagoonTables();
    }
  }, [user, isInitialized, updatePPDLagoonTables]);

  // Fetch account number when user is available
  useEffect(() => {
    if (accountDropdownNumberFeatureFlag && user?.uuid) {
      getUserAccountNumber({});
    }
  }, [user?.uuid]);

  return (
    <ThemeProvider theme={theme}>
      <LagoonProvider value={{ ppdLagoonTables }}>
        <PrintProvider>
          <LoadingOverlay
            isLoading={isContentLoading && !initError}
            loadingTitle="Please wait"
            loadingMessage="Loading application..."
            width="400px"
          >
            <If condition={isInitialized}>
              <App
                digitalSecurityError={digitalSecurityError}
                setDigitalSecurityError={setDigitalSecurityError}
                setIsContentLoading={setIsContentLoading}
              />
            </If>
            <StyledContainer>
              <Choose>
                <When condition={isInitialized}>
                  <Home setDigitalSecurityError={setDigitalSecurityError} />
                  <Router.Outlet />
                  <If
                    condition={
                      app !== 'epv' &&
                      !isUhcpForms &&
                      !isCodeLookup &&
                      !isContentLoading
                    }
                  >
                    <Footer />
                  </If>
                </When>
                <When condition={initError}>
                  <ErrorPage />
                </When>
              </Choose>
            </StyledContainer>
          </LoadingOverlay>
        </PrintProvider>
      </LagoonProvider>
    </ThemeProvider>
  );
};
